import React, { type PropsWithChildren } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Modal, type ModalProps, Pressable, StyleSheet, View } from 'react-native';
import Animated, {
  Easing,
  FadeIn,
  FadeInDown,
  FadeOut,
  FadeOutDown,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { FocusScope, theme, useResponsive } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const BagFeaturedUpsellDialog = (props: Props) => {
  const { isOpen, isContentVisible, accessibilityLabel, children, onRequestClose } = props;

  const { formatMessage } = useIntl();
  const { match } = useResponsive();
  const safeAreaInsets = useSafeAreaInsets();

  // ─── Styles ────────────────────────────────────────────────────────────────

  const safeAreaStyle = useStyle(
    () => ({
      paddingTop: safeAreaInsets.top,
    }),
    [safeAreaInsets.top],
  );

  const containerStyles = match([
    [styles.container, styles.containerXS, safeAreaStyle],
    [styles.container, styles.containerSM],
  ]);
  const dialogStyles = [styles.dialog, match([styles.dialogXS, styles.dialogSM])];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Modal
      transparent
      statusBarTranslucent
      animationType="none"
      visible={isOpen}
      supportedOrientations={SUPPORTED_ORIENTATIONS}
      onRequestClose={onRequestClose}
    >
      <FocusScope lockFocus={false}>
        {isContentVisible ? (
          <Animated.View
            entering={BACKDROP_ENTERING}
            exiting={BACKDROP_EXITING}
            style={styles.backdrop}
          >
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={formatMessage(messages.closeA11yLabel)}
              style={StyleSheet.absoluteFill}
              onPress={onRequestClose}
            />
          </Animated.View>
        ) : null}

        <View pointerEvents="box-none" style={containerStyles}>
          {isContentVisible ? (
            <Animated.View
              accessibilityViewIsModal
              role="dialog"
              aria-modal
              aria-label={accessibilityLabel}
              entering={DIALOG_ENTERING}
              exiting={DIALOG_EXITING}
              style={dialogStyles}
            >
              {children}
            </Animated.View>
          ) : null}
        </View>
      </FocusScope>
    </Modal>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ENTER_DURATION = 540;
const EXIT_DURATION = theme.transitions.short;

const ENTER_DELAY = 50;

const BACKDROP_ENTERING = FadeIn.delay(ENTER_DELAY).duration(ENTER_DURATION);
const BACKDROP_EXITING = FadeOut.duration(EXIT_DURATION);

const DIALOG_ENTERING = FadeInDown.delay(ENTER_DELAY)
  .duration(ENTER_DURATION)
  .easing(Easing.bezier(0.33, 1, 0.68, 1));
const DIALOG_EXITING = FadeOutDown.duration(EXIT_DURATION);

const SUPPORTED_ORIENTATIONS: ModalProps['supportedOrientations'] = ['portrait', 'landscape'];

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  closeA11yLabel: {
    defaultMessage: 'Close',
    description: 'Bag | Featured upsell | Backdrop close a11y label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  backdrop: {
    ...StyleSheet.absoluteFill,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.DARK,
  },
  container: {
    flex: 1,
  },
  containerXS: {
    justifyContent: 'flex-end',
    paddingHorizontal: theme.spacing['4'],
    paddingBottom: theme.spacing['6'],
  },
  containerSM: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing['6'],
  },
  dialog: {
    flexShrink: 1,
    maxHeight: '100%',
    overflow: 'hidden',
    backgroundColor: theme.colors.DARK_KALE,
  },
  dialogXS: {
    width: '100%',
    borderRadius: theme.radius.xlarge,
  },
  dialogSM: {
    width: 600,
    maxWidth: '100%',
    borderRadius: 40,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  isOpen: boolean;
  isContentVisible: boolean;
  accessibilityLabel: string;
  onRequestClose: () => void;
}>;
